import { EventType } from '@oneclick/analytics-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../hooks/useAnalytics';
import { EVENT_NAMES } from '../constants/eventNames';
import { STREAM_STATUS, isStreamingStatus } from '../constants/status';
import { CONVERSATION_MODES } from '../constants/modes';
import defaultChatService from '../services/chatService';
import { transformApiMessagesToUI } from '../utils/messageTransformer';
import { parseToolMode } from '../views/TeacherToolsView/teacherToolsConfig';
import { useAIChatConversation } from '../contexts/AIChatConversation/useAIChatConversation';
import { useAIChatAgentConfig } from '../contexts/AIChatAgentConfig/useAIChatAgentConfig';
import { useAIChatShell } from '../contexts/AIChatShell/useAIChatShell';
import { useAIChatPracticeSession } from '../contexts/AIChatPracticeSession/useAIChatPracticeSession';
import { buildStreamOptions } from './streamOptionsBuilder';
import { useMockData } from './useMockData';
import { usePillManager } from './usePillManager';
import { useStorageSync } from './useStorageSync';
import { useStreamingHandlers } from './useStreamingHandlers';
/**
 * Conversation engine — owns message sending, SSE streaming, conversation loading, and
 * message-queue draining for the active chat exchange
 * @param {Object} options - Configuration options
 * @param {Object} options.chatService - Chat service instance to use (defaults to the core service)
 * @param {Boolean} options.isVisible - Whether the chat container is currently visible
 * @param {Boolean} options.enableMockData - Whether to use mock data for development (defaults to false)
 * @returns {Object} Conversation state and methods
 */
/**
 * Simple storage abstraction for messages
 */
const messageStorage = {
  STORAGE_KEY: 'tangerine_chatbot_messages',

  save: (messages) => {
    try {
      localStorage.setItem(messageStorage.STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      console.warn('Failed to save messages:', error);
    }
  },

  load: () => {
    try {
      const saved = localStorage.getItem(messageStorage.STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Failed to load messages:', error);
      return [];
    }
  },

  clear: () => {
    try {
      localStorage.removeItem(messageStorage.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear messages:', error);
    }
  },
};

function useConversationEngine(options = {}) {
  // ============================================================================
  // SETUP & CONTEXT
  // ============================================================================

  // Get the chat service to use (default or provided)
  const chatService = options.chatService || defaultChatService;
  const isVisible = options.isVisible !== undefined ? options.isVisible : true;
  const enableMockData = options.enableMockData || false;

  // Analytics hook
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation(['chatbot']);

  // Get state from Context
  const {
    currentConversationId,
    setCurrentConversationId,
    streamStatus,
    setStreamStatus,
    chatContext,
    setChatContext,
    mode,
    messageQueue,
    dequeueMessage,
    setCanvasContent,
    conversationData,
    setConversationData,
    clearConversationData,
    setCanvasToolExecuting,
    lastFailedMessage,
    setLastFailedMessage,
    clearLastFailedMessage,
  } = useAIChatConversation();
  const { systemPrompt, model, temperature, maxTokens, enableExternalSearch } = useAIChatAgentConfig();
  const { isDevMode } = useAIChatShell();
  const { activityInstanceId, activityExtraInfo } = useAIChatPracticeSession();

  // Pill management for error actions
  const { addPill, removePillsByType } = usePillManager();

  // Track previous mode to detect actual changes
  const previousModeRef = useRef(mode);

  // Refs for conversation management
  const streamControllerRef = useRef(null);
  const newlyCreatedConversationRef = useRef(null);
  const realMessageIdRef = useRef(null);
  // Conversation id whose first canvas generation has already been reported to analytics
  const trackedInitialGenerationRef = useRef(null);

  // Holds the streaming coalescer's cancel fn (assigned once useStreamingHandlers is created below).
  // Every cleanup path that nulls the streaming message calls this FIRST so a buffered animation
  // frame cannot fire afterwards and resurrect a ghost bubble. Defaults to a noop so it is always
  // safe to invoke.
  const cancelPendingChunkRef = useRef(() => {});

  // Stream session counter - increments on every conversation change to invalidate stale callbacks
  const streamSessionRef = useRef(0);

  // ============================================================================
  // INTERNAL HOOKS (Focused Responsibilities)
  // ============================================================================

  // Handle external message changes from other tabs
  const handleExternalMessagesChange = useCallback(
    (newValue) => {
      if (mode === CONVERSATION_MODES.STUDY && newValue && Array.isArray(newValue)) {
        // This will be handled by useMessageState initialization
      }
    },
    [mode]
  );

  // Setup storage sync for messages
  const { setInternalChange: setMessagesInternalChange } = useStorageSync(messageStorage.STORAGE_KEY, handleExternalMessagesChange);

  // Create the save function with internal change marking
  const saveMessagesWithSync = useCallback(
    (messagesToSave) => {
      try {
        setMessagesInternalChange(true);
        localStorage.setItem(messageStorage.STORAGE_KEY, JSON.stringify(messagesToSave));
        setTimeout(() => setMessagesInternalChange(false), 100);
      } catch (error) {
        console.warn('Failed to save messages:', error);
      }
    },
    [setMessagesInternalChange]
  );

  // Set the save function in messageStorage
  messageStorage.save = saveMessagesWithSync;

  // 1. Load conversation from server
  const { loadConversation, isLoadingConversation, conversationLoadError } = useConversationLoader({
    conversationId: currentConversationId,
    mode,
    chatService,
    setConversationData,
    setCanvasContent,
    clearConversationData,
  });

  // 2. Manage message state (single source of truth)
  const { messages, addMessage, clearMessages } = useMessageState({
    conversationData,
    mode,
    saveMessagesWithSync,
  });

  // 3. Persistence
  useMessagePersistence({ messages, mode, saveMessagesWithSync });

  // Streaming state (will be refactored into useMessageStreaming later)
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [activeToolEvents, setActiveToolEvents] = useState([]);

  /**
   * Cleanup all active streams
   */
  const cleanupStreamingResources = useCallback(() => {
    // Drop any buffered streaming frame first (see cancelPendingChunkRef).
    cancelPendingChunkRef.current();

    // Cancel any active stream
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }

    // Reset conversation status to idle to allow new messages
    setStreamStatus(STREAM_STATUS.IDLE);

    // Clear streaming message to prevent stale content
    setStreamingMessage(null);
  }, [setStreamStatus]);

  /**
   * Cleanup transport resources without touching status
   * Used when preparing a new stream so we don't regress status to IDLE
   */
  const cleanupTransportOnly = useCallback(() => {
    // Drop any frame buffered by the stream being superseded — a stale rAF firing after the next
    // placeholder is set would otherwise land the old stream's text on the new bubble.
    cancelPendingChunkRef.current();
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
  }, []);

  // Use extracted mock data hook
  const { loadMockData, addComplexVisualizationMock } = useMockData({
    enableMockData,
    isVisible,
    setActiveToolEvents,
    setStreamingMessage,
    setStreamStatus,
    setMessages: addMessage, // Using addMessage from useMessageState
  });

  // Use extracted streaming handlers (using addMessage instead of setMessages)
  const { handleStreamStart, handleChunk, handleComplete, handleError, cancelPendingChunk } = useStreamingHandlers({
    streamStatus,
    setStreamStatus,
    setStreamingMessage,
    addMessage,
    setActiveToolEvents,
    newlyCreatedConversationRef,
    cleanupStreamingResources,
    onStreamComplete: options.onStreamComplete,
  });

  // Expose the coalescer's cancel fn to the cleanup paths defined above/below via a stable ref.
  useEffect(() => {
    cancelPendingChunkRef.current = cancelPendingChunk;
  }, [cancelPendingChunk]);

  /**
   * Create a new conversation and set it as current
   */
  const createNewConversation = useCallback(async () => {
    try {
      // Create options object for the conversation
      const conversationOptions = {
        mode: mode, // Use mode from Redux state
        trigger: 'DIRECT',
      };

      // Include activityInstanceId for practice mode conversations
      if (mode === CONVERSATION_MODES.PRACTICE && activityInstanceId) {
        conversationOptions.activityInstanceId = activityInstanceId;
      }

      // Add overrides if in dev mode
      if (isDevMode) {
        // Include system prompt if it's been customized
        if (systemPrompt && systemPrompt !== 'You are a helpful assistant that can answer questions and help with tasks.') {
          conversationOptions.systemPrompt = systemPrompt;
        }

        // Create LLM config with model, temperature, and maxTokens
        conversationOptions.llmConfig = {
          model: model,
          temperature: temperature,
          maxTokens: maxTokens,
        };
      }

      // Add features configuration if external search is enabled
      if (enableExternalSearch) {
        conversationOptions.features = {
          enableExternalSearch: true,
        };
      }

      // Create a new conversation with options
      const conversation = await chatService.createConversation(conversationOptions);

      // Set it as the current conversation (API now returns conversationId instead of id)
      const conversationId = conversation.conversationId || conversation.id;

      // Create initial conversation object for history
      const initialConversation = {
        id: conversationId,
        title: t('New Chat'), // Placeholder until AI generates title
        updatedAt: new Date().toISOString(),
      };

      // Notify parent if callback provided
      if (options.onConversationCreated) {
        options.onConversationCreated(initialConversation);
      }

      // Mark this conversation as newly created to avoid unnecessary loading
      newlyCreatedConversationRef.current = conversationId;

      setCurrentConversationId(conversationId);

      // Track conversation started event
      try {
        await trackEvent(EventType.CONVERSATION_STARTED, {
          conversation_id: conversationId,
          mode: mode,
        });
      } catch (error) {
        console.error('Failed to track conversation started event:', error);
      }

      return conversationId;
    } catch (error) {
      // Log for diagnostics and set error state
      console.error('Error creating conversation:', error);
      setStreamStatus(STREAM_STATUS.ERROR);
      return null;
    }
  }, [
    chatService,
    setCurrentConversationId,
    setStreamStatus,
    isDevMode,
    systemPrompt,
    model,
    temperature,
    maxTokens,
    mode,
    activityInstanceId,
    enableExternalSearch,
    trackEvent,
    options,
  ]);

  // ============================================================================
  // CONVERSATION LOADING LOGIC
  // ============================================================================

  // Handle conversation ID changes and load persisted messages
  useEffect(() => {

    // 🎭 Si está en modo mock, no procesar lógica normal de conversación
    if (enableMockData) {
      return;
    }

    // If conversation ID is cleared (new chat), clear messages
    if (!currentConversationId) {
      // Clean up any active streaming before clearing messages
      cleanupStreamingResources();

      clearMessages(); // Use useMessageState clearMessages
      setStreamingMessage(null);
      setActiveToolEvents([]);
      setCanvasContent('');
      clearConversationData();
      return;
    }

    // Skip loading if this conversation was just created
    if (newlyCreatedConversationRef.current === currentConversationId) {
      // Clear the ref to allow future loads of the same conversation
      newlyCreatedConversationRef.current = null;
      return;
    }

    // Skip if conversation already loaded (prevents clearing messages during queue processing)
    if (conversationData?.id === currentConversationId) {
      return;
    }

    // Load conversation details when conversation ID changes
    if (isVisible) {
      loadConversation(currentConversationId);
    }
  }, [isVisible, mode, enableMockData, currentConversationId, cleanupStreamingResources, loadConversation, clearMessages]);

  // Track previous conversation ID to detect cold start vs user switch
  const previousConversationIdRef = useRef(currentConversationId);

  // Invalidate streams and reset state when conversation changes
  // This ensures any in-flight stream callbacks are ignored after conversation switch
  // BUT skip invalidation on cold start (null → valid ID) since we just created the conversation
  useEffect(() => {
    const previousId = previousConversationIdRef.current;
    previousConversationIdRef.current = currentConversationId;

    // Skip invalidation when transitioning from null (cold start)
    // We just created this conversation and are about to stream to it
    if (previousId === null && currentConversationId !== null) {
      return;
    }

    // Increment session to invalidate any in-flight stream callbacks
    streamSessionRef.current += 1;

    // Immediately clear streaming state to prevent stale content from showing
    cancelPendingChunkRef.current();
    setStreamingMessage(null);
    setActiveToolEvents([]);
    setStreamStatus(STREAM_STATUS.IDLE);
  }, [currentConversationId, setStreamStatus]);

  // Clear messages when mode ACTUALLY changes (not on every render)
  useEffect(() => {
    // Only clear if mode actually changed from a different value
    if (previousModeRef.current !== mode) {

      // Clear all conversation data when mode changes
      clearMessages(); // Use useMessageState clearMessages
      cancelPendingChunkRef.current();
      setStreamingMessage(null);
      setActiveToolEvents([]);

      // Clear storage based on mode
      if (mode === CONVERSATION_MODES.PRACTICE) {
        // Always clear storage in practice mode
        messageStorage.clear();
      }

      // Update the ref to current mode
      previousModeRef.current = mode;
    }
  }, [mode, clearMessages]);

  // Clean up stream controller when component becomes not visible
  useEffect(() => {
    // 🎭 Si está en modo mock, no limpiar automáticamente
    if (enableMockData) return;

    // Only cleanup when transitioning from visible to not visible while streaming
    if (!isVisible && isStreamingStatus(streamStatus)) {
      // Cancel any active stream
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }

      // Reset states
      cancelPendingChunkRef.current();
      setStreamStatus(STREAM_STATUS.IDLE);
      setStreamingMessage(null);
    }
  }, [isVisible, streamStatus, setStreamStatus, enableMockData]); // Watch visibility and status changes

  // Separate effect for cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up on unmount only
      cancelPendingChunkRef.current();
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }
    };
  }, []); // Empty deps - only run on mount/unmount

  /**
   * Explicitly create a new chat - clears the state and optionally updates context
   */
  const startNewChat = useCallback(() => {
    // First update context if getChatContextData is available
    if (options.getChatContextData && typeof options.getChatContextData === 'function') {
      try {
        const newContext = options.getChatContextData();
        if (newContext && typeof newContext === 'object') {
          setChatContext(newContext);
        } else {
          console.error('useConversationEngine: getChatContextData() did not return a valid object.');
        }
      } catch (error) {
        console.error('useConversationEngine: Error calling getChatContextData():', error);
      }
    }

    // Reset the current UI state
    clearMessages(); // Use useMessageState clearMessages
    setStreamingMessage(null);
    setStreamStatus(STREAM_STATUS.IDLE);
    setActiveToolEvents([]);
    setCanvasContent('');

    // Clear persisted messages in study mode
    if (mode === CONVERSATION_MODES.STUDY) {
      messageStorage.clear();
    }

    // Clean up streaming resources
    cleanupStreamingResources();

    // Clear the conversation ID and newly created tracking
    setCurrentConversationId(null);
    newlyCreatedConversationRef.current = null;
  }, [setCurrentConversationId, setStreamStatus, cleanupStreamingResources, setChatContext, options, mode, clearMessages]);

  /**
   * Set up and initiate a streaming response
   */
  const initiateStream = useCallback(
    async (message, imageFile, botMessageId, globalChatContext, messageOptions = {}) => {
      // Clean up any previous transport resources first (do not change status here)
      cleanupTransportOnly();

      // Reset the real message ID ref for this new stream
      realMessageIdRef.current = null;

      // Check if we need to create a new conversation first
      // IMPORTANT: This must happen BEFORE capturing the session, because createNewConversation()
      // triggers a context update (setCurrentConversationId) which increments streamSessionRef.
      // If we capture session first, it becomes stale immediately after conversation creation.
      let conversationForMessage = currentConversationId;

      // One failure event per attempt: the transport may report the same failure more than once
      let hasReportedFailure = false;
      const reportTutorMessageFailed = () => {
        if (hasReportedFailure) return;
        hasReportedFailure = true;
        trackEvent(EVENT_NAMES.TUTOR_MESSAGE_FAILED, { mode }).catch((trackingError) => console.error('Analytics tracking failed:', trackingError));
      };

      if (!conversationForMessage) {
        // In teacher-tools, conversationId must come from the URL/provider; do not auto-create
        if (mode === CONVERSATION_MODES.TEACHER_TOOLS) {
          handleError(new Error('Missing conversation id for teacher-tools'), null, setStreamingMessage, setStreamStatus, streamControllerRef);
          return;
        }
        try {
          conversationForMessage = await createNewConversation();
          if (!conversationForMessage) {
            throw new Error('Failed to create conversation');
          }
        } catch (error) {
          handleError(error, null, setStreamingMessage, setStreamStatus, streamControllerRef);
          reportTutorMessageFailed();
          return;
        }
      }

      // Initial generation = the instance's first canvas stream. Regenerations and follow-up
      // messages are tracked by their own events, so they must not report tool output here.
      const isInitialToolGeneration =
        mode === CONVERSATION_MODES.TEACHER_TOOLS && (conversationData?.messages?.length ?? 0) === 0 && trackedInitialGenerationRef.current !== conversationForMessage;
      const toolType = parseToolMode(conversationData?.mode) || '';

      // Capture session AFTER conversation is confirmed to exist
      // This ensures the session matches the current state after any context updates
      const streamSession = streamSessionRef.current;

      // Helper to check if this stream is still valid (conversation hasn't changed)
      const isStale = () => streamSession !== streamSessionRef.current;

      // Handler for when backend sends the real message ID
      const handleMessageComplete = (messageId) => {
        if (isStale()) return; // Ignore if conversation changed
        realMessageIdRef.current = messageId;
      };

      // Wrapped chunk handler with staleness check
      const wrappedHandleChunk = (chunk) => {
        if (isStale()) {
          return;
        }
        handleChunk(chunk);
      };

      // Set up callbacks for streaming (using extracted handlers)
      const wrappedHandleComplete = (fullText) => {
        // Guard: Only process if conversation hasn't changed
        if (isStale()) {
          // Still clean up resources even if stale
          cleanupStreamingResources();
          return;
        }
        // Clear the failed message since stream completed successfully
        clearLastFailedMessage();
        // Clear error action pills on success
        removePillsByType('error-action');
        // Pass the real message ID (if received) to handleComplete
        handleComplete(fullText, botMessageId, globalChatContext, conversationForMessage, realMessageIdRef.current);
        // Reset the ref after use
        realMessageIdRef.current = null;
        // Clear canvas tool executing flag and remove canvas tool for teacher-tools mode
        if (mode === CONVERSATION_MODES.TEACHER_TOOLS) {
          // Immediately remove canvas tool to prevent it transferring to the new final message
          // (activeTools is passed to last message, and the streaming message is about to be replaced)
          setActiveToolEvents((prev) => prev.filter((event) => event.toolName !== 'canvas_generation'));
          setCanvasToolExecuting(false);

          if (isInitialToolGeneration) {
            trackedInitialGenerationRef.current = conversationForMessage;
            trackEvent(EVENT_NAMES.TOOL_OUTPUT_GENERATED, { tool_type: toolType }).catch((error) => console.error('Analytics tracking failed:', error));
          }
        }
      };

      const wrappedHandleError = (error) => {
        // Guard: Only show error if conversation hasn't changed
        if (isStale()) {
          return;
        }
        handleError(error, currentConversationId, setStreamingMessage, setStreamStatus, streamControllerRef);
        reportTutorMessageFailed();
        // No retry - let the error message show to the user
        // Clear canvas tool executing flag and mark canvas tool as failed for teacher-tools mode
        if (mode === CONVERSATION_MODES.TEACHER_TOOLS) {
          // Mark the canvas tool as failed
          setActiveToolEvents((prev) => prev.map((event) => (event.toolName === 'canvas_generation' ? { ...event, status: 'failed' } : event)));
          setCanvasToolExecuting(false);

          // Not marked as tracked on failure so a successful retry still reports the generation
          if (isInitialToolGeneration) {
            trackEvent(EVENT_NAMES.TOOL_OUTPUT_GENERATION_FAILED, { error_type: error?.name || 'unknown_error', tool_type: toolType }).catch((trackingError) =>
              console.error('Analytics tracking failed:', trackingError)
            );
          }
        }
      };

      // Initiate streaming with appropriate endpoint
      try {
        // Stream to conversation and get controller to abort if needed

        // Build stream options using extracted utility
        const streamOptions = buildStreamOptions({
          mode,
          globalChatContext,
          isDevMode,
          model,
          temperature,
          maxTokens,
          systemPrompt,
          automaticMessage: messageOptions.automaticMessage,
          studentResponse: messageOptions.studentResponse,
        });

        // Track if this is the first canvas delta for this stream
        let isFirstCanvasDelta = true;

        const streamController = await chatService.streamChatResponseForConversation(
          conversationForMessage,
          message,
          streamOptions,
          wrappedHandleChunk,
          wrappedHandleComplete,
          wrappedHandleError,
          imageFile,
          // Pass canvas handler only for teacher-tools mode
          mode === CONVERSATION_MODES.TEACHER_TOOLS
            ? (fullContent) => {
                // Guard: Ignore canvas updates if conversation changed
                if (isStale()) {
                  return;
                }

                // On first canvas delta, set status to STREAMING and inject tool event
                if (isFirstCanvasDelta) {
                  setStreamStatus(STREAM_STATUS.STREAMING);

                  // Inject canvas tool event on first delta (not in sendMessage)
                  // This ensures loading indicator only shows when canvas is actually being generated
                  setActiveToolEvents((prev) => [
                    ...prev,
                    {
                      type: 'tool_invocation',
                      toolName: 'canvas_generation',
                      toolType: 'canvas_generation',
                      status: 'started',
                      timestamp: new Date().toISOString(),
                      id: `canvas-generation-${Date.now()}`,
                    },
                  ]);

                  isFirstCanvasDelta = false;
                }

                // Update persistent canvas state
                setCanvasContent(fullContent || '');
              }
            : null,
          // Pass handler for capturing real message ID from backend
          handleMessageComplete
        );

        // Track analytics event for message sent
        try {
          const analyticsEventPayload = {
            conversation_id: conversationForMessage,
            mode: mode,
          };

          // Add kc_id, kc_reference and activity_id for practice mode
          if (mode === CONVERSATION_MODES.PRACTICE) {
            // Always include these fields for practice mode, use empty string if not defined
            analyticsEventPayload.activity_id = activityInstanceId || '';
            analyticsEventPayload.kc_id = activityExtraInfo?.associatedKcId || '';
            analyticsEventPayload.kc_reference = activityExtraInfo?.associatedKcReferenceId || '';
          }

          await trackEvent(EVENT_NAMES.STUDENT_MESSAGE_SENT, analyticsEventPayload);
        } catch (analyticsError) {
          console.error('Failed to track message sent event:', analyticsError);
          // Don't throw the error to avoid breaking the chat functionality
        }

        // Save the controller for possible abort
        streamControllerRef.current = streamController;
      } catch (error) {
        wrappedHandleError(error);
      }
    },
    [
      chatService,
      streamStatus,
      currentConversationId,
      conversationData,
      createNewConversation,
      cleanupTransportOnly,
      setStreamStatus,
      handleStreamStart,
      handleChunk,
      handleComplete,
      handleError,
      systemPrompt,
      isDevMode,
      model,
      temperature,
      maxTokens,
      mode,
      setCanvasToolExecuting,
      setCanvasContent,
      clearLastFailedMessage,
      removePillsByType,
      trackEvent,
    ]
  );

  /**
   * Handle sending a message, with streaming response
   */
  const sendMessage = useCallback(
    async (message, imageFile = null, options = {}) => {
      // Don't process messages if component isn't visible
      // Allow empty messages for teacher-tools mode to trigger initial stream
      if (!isVisible) return;
      if (!message.trim() && !imageFile && mode !== CONVERSATION_MODES.TEACHER_TOOLS) return;

      const { automaticMessage, studentResponse, displayText } = options;

      // Get the updated context from the parent component using getChatContextData
      // This ensures we have the most recent course ID and other context values
      let globalChatContext = chatContext;

      // If the parent provided a context getter function, use it
      if (options.getChatContextData && typeof options.getChatContextData === 'function') {
        try {
          const updatedContext = options.getChatContextData();
          if (updatedContext && typeof updatedContext === 'object') {
            globalChatContext = updatedContext;
          }
        } catch (error) {
          // Intentionally ignore context update errors
          void error;
        }
      }

      // Create message object for the UI
      const userMessage = {
        id: Date.now(),
        text: displayText || message,
        sender: 'user',
        timestamp: new Date(),
        context: globalChatContext,
        image: imageFile ? URL.createObjectURL(imageFile) : null,
        imageName: imageFile ? imageFile.name : null,
      };

      // Add message to UI immediately using useMessageState
      addMessage(userMessage);

      // Store message data for potential retry
      setLastFailedMessage({ message, imageFile });

      // Reset streaming state
      setStreamingMessage(null);
      handleStreamStart();

      // Set canvas tool executing flag (only for teacher-tools mode)
      // Note: Tool event injection moved to canvas delta handler (on first delta arrival)
      if (mode === CONVERSATION_MODES.TEACHER_TOOLS) {
        setCanvasToolExecuting(true);
      }

      // Create a placeholder for the bot response
      const botMessageId = Date.now() + 1;
      const placeholder = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
        context: globalChatContext,
      };

      setStreamingMessage(placeholder);

      // Start the stream with image file
      await initiateStream(message, imageFile, botMessageId, globalChatContext, {
        automaticMessage,
        studentResponse,
      });
    },
    [chatContext, isVisible, setStreamStatus, initiateStream, options, mode, addMessage, setCanvasToolExecuting, setLastFailedMessage]
  );

  const isStreaming = isStreamingStatus(streamStatus);

  // 4. Process message queue
  useMessageQueue({
    isVisible,
    messageQueue,
    isStreaming,
    sendMessage,
    dequeueMessage,
    mode,
    activityInstanceId,
    currentConversationId,
    messages,
  });

  /**
   * Load previous conversations for the user
   */
  const loadConversations = useCallback(async () => {
    try {
      const response = await chatService.getAllConversations();
      return response.conversations || [];
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  }, [chatService, mode]);

  /**
   * Retry the last failed message
   */
  const retryLastMessage = useCallback(() => {
    if (!lastFailedMessage) {
      console.warn('[useConversationEngine] No failed message to retry');
      return;
    }

    // Clear error pills before retrying
    removePillsByType('error-action');

    // Persist the error message in conversation history before clearing
    if (streamingMessage && streamingMessage.isError) {
      addMessage({
        id: streamingMessage.id || Date.now(),
        text: streamingMessage.text,
        sender: 'bot',
        timestamp: streamingMessage.timestamp || new Date(),
        isStreaming: false,
        isError: true,
      });
    }

    // Clear the current streaming/error message
    setStreamingMessage(null);

    // Re-send the original failed message to the backend,
    // but display the pill text in the user bubble
    const { message, imageFile } = lastFailedMessage;
    sendMessage(message, imageFile, { displayText: t('Yes, try again') });
  }, [lastFailedMessage, sendMessage, removePillsByType, setStreamingMessage, streamingMessage, addMessage, t]);

  // Store retryLastMessage in a ref to avoid dependency issues (prevents infinite loop)
  const retryLastMessageRef = useRef(retryLastMessage);
  useEffect(() => {
    retryLastMessageRef.current = retryLastMessage;
  }, [retryLastMessage]);

  // Track if we've already added the error pill to prevent duplicate additions
  const errorPillAddedRef = useRef(false);

  // Effect to manage error pills based on conversation status
  // Note: addPill, removePillsByType, and t are stable (derived from dispatch/i18n)
  // and intentionally excluded from dependencies to avoid unnecessary re-runs
  useEffect(() => {
    const shouldShowErrorPill = streamStatus === STREAM_STATUS.ERROR && lastFailedMessage;

    if (shouldShowErrorPill && !errorPillAddedRef.current) {
      // Add retry pill on error (only once)
      errorPillAddedRef.current = true;
      addPill({
        id: 'retry',
        type: 'error-action',
        icon: 'thumbs-up',
        text: t('Yes, try again'),
        onClick: () => retryLastMessageRef.current(),
        variant: 'primary',
        priority: 10,
      });
    } else if (!shouldShowErrorPill && errorPillAddedRef.current) {
      // Remove error pills when no longer in error state
      errorPillAddedRef.current = false;
      removePillsByType('error-action');
    }
  }, [streamStatus, lastFailedMessage]);

  return {
    messages,
    streamingMessage,
    activeToolEvents,
    isStreaming,
    streamStatus,
    sendMessage,
    loadConversations,
    startNewChat,
    createNewConversation,
    loadMockData,
    addComplexVisualizationMock,
    isLoadingConversation,
    conversationLoadError,
    retryLastMessage,
  };
}

// =============================================================================
// INTERNAL FOCUSED HOOKS
// =============================================================================

/**
 * Hook for loading conversation from API
 * Responsibility: Fetch conversation data from server
 * Does NOT modify messages state
 */
function useConversationLoader({ conversationId, mode, chatService, setConversationData, setCanvasContent, clearConversationData }) {
  // Use context state for isLoadingConversation (allows external control via setIsLoadingConversation)
  const { isLoadingConversation, setIsLoadingConversation } = useAIChatConversation();
  const [conversationLoadError, setConversationLoadError] = useState(null);
  const loadedConversationIdRef = useRef(null);

  const loadConversation = useCallback(
    async (id) => {
      // Skip if already loaded
      if (loadedConversationIdRef.current === id) {
        return null;
      }

      setConversationLoadError(null);
      setIsLoadingConversation(true);

      try {
        // Fetch conversation details
        const conversationDetails = await chatService.getConversation(id, mode);

        // Store full conversation object in context
        setConversationData(conversationDetails);

        // Set canvas content for teacher-tools mode
        if (mode === CONVERSATION_MODES.TEACHER_TOOLS && conversationDetails.canvas) {
          setCanvasContent(conversationDetails.canvas.content || '');
        }

        // Mark as loaded
        loadedConversationIdRef.current = id;

        return conversationDetails;
      } catch (error) {
        console.error('[useConversationLoader] Error loading conversation:', error);
        setConversationLoadError('An error occurred. Please try again.');
        return null;
      } finally {
        setIsLoadingConversation(false);
      }
    },
    [chatService, mode, setConversationData, setCanvasContent]
  );

  // Clear loaded ref when conversation cleared
  useEffect(() => {
    if (!conversationId) {
      loadedConversationIdRef.current = null;
      clearConversationData();
    }
  }, [conversationId, clearConversationData]);

  return {
    loadConversation,
    isLoadingConversation,
    conversationLoadError,
  };
}

/**
 * Hook for managing message state (single source of truth)
 * Responsibility: Manage local messages array
 * Initialize once from server data, then append-only
 */
function useMessageState({ conversationData, mode, saveMessagesWithSync }) {
  const [messages, setMessages] = useState([]);
  const initializedConversationRef = useRef(null);

  // Initialize messages from conversationData (ONCE per conversation)
  useEffect(() => {
    if (conversationData?.id && initializedConversationRef.current !== conversationData.id) {
      const transformedMessages = transformApiMessagesToUI(conversationData.messages || []);
      setMessages(transformedMessages);
      initializedConversationRef.current = conversationData.id;

      // Update localStorage in study mode
      if (mode === CONVERSATION_MODES.STUDY && transformedMessages.length > 0) {
        saveMessagesWithSync(transformedMessages);
      }
    }
  }, [conversationData, mode, saveMessagesWithSync]);

  // Add message (append-only)
  const addMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Clear messages (explicit user action only)
  const clearMessages = useCallback(() => {
    setMessages([]);
    initializedConversationRef.current = null;
  }, []);

  return {
    messages,
    addMessage,
    clearMessages,
  };
}

/**
 * Hook for processing message queue
 * Responsibility: Process queued messages and trigger sending
 */
function useMessageQueue({ isVisible, messageQueue, isStreaming, sendMessage, dequeueMessage, mode, activityInstanceId, currentConversationId, messages }) {
  useEffect(() => {
    // Only process queue when visible, not streaming, and has messages
    if (isVisible && messageQueue.length > 0 && !isStreaming) {
      // For teacher-tools ensure we have a conversationId before sending queued messages
      if (mode === CONVERSATION_MODES.TEACHER_TOOLS && !currentConversationId) {
        return;
      }

      const { message, imageFile, options } = messageQueue[0];

      // Defensive: drop duplicate empty init messages after first response
      if (mode === CONVERSATION_MODES.TEACHER_TOOLS && (!message || !message.trim()) && messages.length > 0) {
        dequeueMessage();
        return;
      }

      // Check if the queued message belongs to the current activity (practice mode only)
      if (mode === CONVERSATION_MODES.PRACTICE && options?.activityInstanceId && options.activityInstanceId !== activityInstanceId) {
        // Message is from a different activity, drop it
        dequeueMessage();
        return;
      }

      // Send the message with its options
      sendMessage(message, imageFile, options);

      // Remove the message from queue
      dequeueMessage();
    }
  }, [isVisible, messageQueue, isStreaming, sendMessage, dequeueMessage, mode, activityInstanceId, currentConversationId, messages]);
}

/**
 * Hook for message persistence to localStorage
 * Responsibility: Sync messages to localStorage (study mode only)
 */
function useMessagePersistence({ messages, mode, saveMessagesWithSync }) {
  // Save messages to storage when they change (only in study mode)
  useEffect(() => {
    if (mode === CONVERSATION_MODES.STUDY && messages.length > 0) {
      saveMessagesWithSync(messages);
    }
  }, [mode, messages, saveMessagesWithSync]);
}

export default useConversationEngine;
